import {
  Button,
  Card,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from "@mui/material";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { useContext, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { cloneDeep } from "lodash";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import DesignPost from "./DesignPost";
import { SocialCampaignContext } from "../Context";
import DesignSection from "./DesignSection";
import PostSection from "./PostSection";
import Schedule from "./Schedule";
import strings from "global/constants/StringConstants";
import { socialCampaignExistsCheck } from "screens/Integration/Component/FacebookService";
import CustomLoader from "global/components/CustomLoader/CustomLoader";

const Step2 = () => {
  const [isDesignPostModalOpen, setIsDesignPostModalOpen] = useState(false);
  const { submitCampaign, setSubmitCampaign } = useContext(
    SocialCampaignContext
  );
  const [editName, setEditName] = useState(false);
  const [schedule, setSchedule] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [carousalIndex, setCarousalIndex] = useState<number>(0);
  const [postImage, setPostImage] = useState<string[]>([]);
  const [formFields, setFormFields] = useState({
    name: {
      value: submitCampaign.name ?? "",
      error: "",
    },
    selectedPlatform: {
      value: submitCampaign.socialAppName ?? "",
      error: "",
    },
    selectedPage: {
      label: {
        value: submitCampaign.pageName ?? "",
        error: "",
      },
      pageId: {
        value: submitCampaign.socialCampaign.pageId ?? "",
        error: "",
      },
      profilePicture: {
        value: submitCampaign.profilePicture ?? "",
        error: "",
      },
      userName: {
        value: submitCampaign.userName ?? "",
        error: "",
      },
    },
    message: {
      value: "",
      error: "",
    },
    image: {
      value: [],
      error: "",
    },
  });

  const saveCampaignName = () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    if (!isTruthy(clonedFormFields.name.value)) {
      error = true;
      clonedFormFields.name.error = "Please enter the campaign name";
    }
    if (error) {
      return setFormFields(clonedFormFields);
    }
    setSubmitCampaign({
      ...submitCampaign,
      name: formFields.name.value.trim(),
    });
    setEditName(false);
  };

  const handleValidation = () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    if (
      submitCampaign.socialAppName === strings.FACEBOOK &&
      !isTruthy(clonedFormFields.message.value) &&
      clonedFormFields.image.value.length === 0
    ) {
      error = true;
      clonedFormFields.message.error =
        "Please design your post content before proceeding.";
      clonedFormFields.image.error =
        "Please either enter content or upload image or both";
    }
    if (
      submitCampaign.socialAppName === strings.INSTAGRAM &&
      clonedFormFields.image.value.length === 0
    ) {
      error = true;
      clonedFormFields.image.error =
        "Please design your post content before proceeding.";
    }
    if (error) {
      setFormFields(clonedFormFields);
      return !error;
    }
    return error;
  };

  const submitSocialCampaign = async () => {
    try {
      if (handleValidation()) {
        return;
      }
      setLoading(true);
      await socialCampaignExistsCheck(
        submitCampaign.name.trim(),
        submitCampaign.socialAppName
      );
      setLoading(false);
      setSchedule(true);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const getHeader = () => {
    return (
      <Stack direction="row" px={2} justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          {/* <IconButton>
            <SvgIcon>
              <KeyboardBackspaceIcon fontSize="small" />
            </SvgIcon>
          </IconButton> */}
          {editName ? (
            <TextField
              size="small"
              variant="outlined"
              placeholder="Campaign Name"
              value={formFields.name.value}
              onChange={(event: any) => {
                setFormFields({
                  ...formFields,
                  name: {
                    value: event.target.value,
                    error: "",
                  },
                });
              }}
              required
              error={isTruthy(formFields.name.error)}
              inputProps={{ minLength: 1, maxLength: 90 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span style={{ fontSize: "0.75rem" }}>
                      {formFields.name?.value?.length} / 90
                    </span>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <Typography
              variant="h4"
              noWrap
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              maxWidth="250px"
              display="block"
            >
              {submitCampaign.name}
            </Typography>
          )}
          {editName ? (
            <>
              <IconButton onClick={saveCampaignName}>
                <SvgIcon>
                  <CheckIcon fontSize="small" />
                </SvgIcon>
              </IconButton>
              <IconButton onClick={() => setEditName(false)}>
                <SvgIcon>
                  <CloseIcon fontSize="small" />
                </SvgIcon>
              </IconButton>
            </>
          ) : (
            <IconButton
              onClick={() => {
                setFormFields({
                  ...formFields,
                  name: {
                    error: "",
                    value: submitCampaign.name,
                  },
                });
                setEditName(true);
              }}
            >
              <SvgIcon>
                <ModeEditIcon fontSize="small" />
              </SvgIcon>
            </IconButton>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            disabled={
              submitCampaign.socialAppName === strings.FACEBOOK
                ? submitCampaign.socialCampaign.link.length === 0 ||
                  !submitCampaign.socialCampaign.message
                : submitCampaign.socialCampaign.link.length === 0
            }
            onClick={() => submitSocialCampaign()}
          >
            Schedule
          </Button>
        </Stack>
      </Stack>
    );
  };

  const getBody = () => {
    return (
      <Card>
        <Stack spacing={4} py={4} divider={<Divider />}>
          <PostSection formFields={formFields} setFormFields={setFormFields} />
          <DesignSection
            setIsDesignPostModalOpen={setIsDesignPostModalOpen}
            socialCampaign={submitCampaign.socialCampaign}
            formFields={formFields}
            setFormFields={setFormFields}
            carousalIndex={carousalIndex}
            setCarousalIndex={setCarousalIndex}
            postImage={submitCampaign.socialCampaign.link}
          />
        </Stack>
      </Card>
    );
  };

  const getDesignPostModal = () => {
    return (
      <DesignPost
        handleDialogClose={() => {
          setIsDesignPostModalOpen(false);
          setFormFields((prevFormFields) => ({
            ...prevFormFields,
            message: {
              ...prevFormFields.message,
              error: "",
            },
            image: {
              ...prevFormFields.image,
              error: "",
            },
          }));
        }}
        isDialogOpen={isDesignPostModalOpen}
        formFields={formFields}
        setFormFields={setFormFields}
        carousalIndex={carousalIndex}
        setCarousalIndex={setCarousalIndex}
        postImage={postImage}
        setPostImage={setPostImage}
      />
    );
  };

  const getScheduleModal = () => {
    return (
      <Schedule
        handleDialogClose={() => setSchedule(false)}
        isDialogOpen={schedule}
      />
    );
  };

  const getPage = () => {
    return (
      <Stack spacing={4}>
        {getHeader()}
        {getBody()}
      </Stack>
    );
  };

  return (
    <>
      {getPage()}
      {getDesignPostModal()}
      {getScheduleModal()}
      <CustomLoader isLoading={loading} />
    </>
  );
};

export default Step2;
